import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  X,
  ShieldCheck,
  Loader2,
  ArrowRight,
  Zap,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { API_BASE } from '../config/api';
import { useAuth } from '../auth/AuthContext';

export interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'plan' | 'response_addon' | 'whatsapp_addon';
  targetId: string;
  title: string;
  amountXof: number;
  billingCycle?: 'monthly' | 'annual';
  onNavigate?: (tab: string) => void;
  onSuccess?: () => void;
}

const SUPPORTED_OPERATORS = [
  { name: 'Wave', logo: '/wave-logo.png' },
  { name: 'Orange Money', logo: '/orange-money-logo.png' },
  { name: 'MTN MoMo', logo: '/mtn-logo.png' },
  { name: 'Moov Money', logo: '/moov-money-logo.png' },
];

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  type,
  targetId,
  title,
  amountXof,
  billingCycle = 'monthly',
  onNavigate,
  onSuccess
}) => {
  const { user, refreshUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeOrderRef, setActiveOrderRef] = useState<string | null>(null);
  const [activePaymentUrl, setActivePaymentUrl] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{
    status: 'paid' | 'pending' | 'cancelled' | 'failed';
    message?: string;
    amount?: number;
  } | null>(null);

  // Vérification de statut auprès de la passerelle
  const verifyPaymentStatus = useCallback(async (orderRef: string, showCheckingState = true) => {
    if (!orderRef) return;
    if (showCheckingState) {
      setIsCheckingStatus(true);
    }
    setErrorMsg(null);

    try {
      const res = await fetch(`${API_BASE}/billing/payment/status/${encodeURIComponent(orderRef)}?sync=true`, {
        credentials: 'include'
      });
      const json = await res.json();

      if (json && json.success && json.data) {
        const tx = json.data;
        if (tx.status === 'paid') {
          setVerificationResult({
            status: 'paid',
            message: `Paiement de ${Number(tx.amount || amountXof).toLocaleString('fr-FR')} FCFA validé avec succès !`,
            amount: tx.amount || amountXof
          });
          try {
            sessionStorage.removeItem('zeyilo_pending_payment');
          } catch (_) {}
          if (refreshUser) {
            refreshUser().catch(() => {});
          }
          if (onSuccess) {
            onSuccess();
          }
          return;
        }

        if (tx.status === 'cancelled') {
          setVerificationResult({
            status: 'cancelled',
            message: "La transaction a été annulée. Aucun montant n'a été débité de votre compte."
          });
          try {
            sessionStorage.removeItem('zeyilo_pending_payment');
          } catch (_) {}
          return;
        }

        if (tx.status === 'failed' || tx.status === 'expired' || tx.status === 'amount_mismatch') {
          setVerificationResult({
            status: 'failed',
            message: `Le paiement n'a pas abouti (${tx.status}). Aucun montant n'a été prélevé.`
          });
          try {
            sessionStorage.removeItem('zeyilo_pending_payment');
          } catch (_) {}
          return;
        }

        // Sinon statut encore 'pending' (attente de code secret Mobile Money)
        setVerificationResult({
          status: 'pending',
          message: "Paiement en cours de traitement ou en attente de validation Mobile Money."
        });
      } else {
        setVerificationResult({
          status: 'pending',
          message: "Transaction en attente de confirmation par l'opérateur."
        });
      }
    } catch (err: any) {
      console.error('[PaymentModal] Verification error:', err);
      setVerificationResult({
        status: 'pending',
        message: "Impossible de joindre la passerelle. Vous pouvez revérifier dans un instant."
      });
    } finally {
      setIsCheckingStatus(false);
      setIsSubmitting(false);
    }
  }, [amountXof, refreshUser, onSuccess]);

  // Récupération de session et gestion du retour navigateur (BFCache / pageshow / visibilitychange / focus)
  useEffect(() => {
    // 1. Au montage ou quand le modal s'ouvre : vérifier s'il existe une session en attente
    try {
      const raw = sessionStorage.getItem('zeyilo_pending_payment');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.orderRef && (Date.now() - (parsed.timestamp || 0) < 2 * 3600 * 1000)) {
          setActiveOrderRef(parsed.orderRef);
          if (parsed.paymentUrl) setActivePaymentUrl(parsed.paymentUrl);
          if (isOpen && parsed.targetId === targetId) {
            verifyPaymentStatus(parsed.orderRef, true);
          }
        }
      }
    } catch (_) {}

    // 2. Détection du retour arrière dans l'historique (BFCache / bouton retour mobile)
    const handleReturnToPage = () => {
      // Débloquer immédiatement tout spinner résiduel
      setIsSubmitting(false);

      let refToCheck = activeOrderRef;
      if (!refToCheck) {
        try {
          const raw = sessionStorage.getItem('zeyilo_pending_payment');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.orderRef) {
              refToCheck = parsed.orderRef;
              setActiveOrderRef(parsed.orderRef);
              if (parsed.paymentUrl) setActivePaymentUrl(parsed.paymentUrl);
            }
          }
        } catch (_) {}
      }

      if (refToCheck && isOpen) {
        verifyPaymentStatus(refToCheck, true);
      }
    };

    const handlePageShow = (_e: PageTransitionEvent) => {
      handleReturnToPage();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleReturnToPage();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleReturnToPage);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleReturnToPage);
    };
  }, [isOpen, targetId, activeOrderRef, verifyPaymentStatus]);

  // Fermeture par touche Echap (toujours autorisée)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleReset = () => {
    setVerificationResult(null);
    setIsSubmitting(false);
    setIsCheckingStatus(false);
    setErrorMsg(null);
    setActiveOrderRef(null);
    setActivePaymentUrl(null);
    try {
      sessionStorage.removeItem('zeyilo_pending_payment');
    } catch (_) {}
  };

  const handleModalClose = () => {
    if (verificationResult?.status === 'paid') {
      onClose();
      if (onNavigate) {
        onNavigate('billing');
      } else {
        window.location.href = '/billing';
      }
    } else {
      onClose();
    }
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isCheckingStatus) return;

    setIsSubmitting(true);
    setErrorMsg(null);
    setVerificationResult(null);

    const customerPhone = user?.phone?.trim() || '0700000000';
    const customerName = user?.fullName?.trim() || user?.businessName?.trim() || user?.email?.split('@')[0] || 'Client Zeyilo';

    let deviceFp = '';
    try {
      deviceFp = localStorage.getItem('zeyilo_device_fp') || '';
      if (!deviceFp) {
        deviceFp = `dfp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 10)}`;
        localStorage.setItem('zeyilo_device_fp', deviceFp);
      }
    } catch {
      deviceFp = 'unknown';
    }

    try {
      const res = await fetch(`${API_BASE}/billing/payment/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-device-fingerprint': deviceFp
        },
        credentials: 'include',
        body: JSON.stringify({
          type,
          targetId,
          billingCycle,
          customerPhone,
          customerName,
          deviceFingerprint: deviceFp
        })
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Impossible d'initier la session de paiement.");
      }

      if (json.data && json.data.paymentUrl) {
        const orderRef = json.data.orderReference;
        const pUrl = json.data.paymentUrl;

        setActiveOrderRef(orderRef);
        setActivePaymentUrl(pUrl);

        try {
          sessionStorage.setItem('zeyilo_pending_payment', JSON.stringify({
            orderRef,
            paymentUrl: pUrl,
            targetId,
            type,
            title,
            amountXof,
            timestamp: Date.now()
          }));
        } catch (_) {}

        // Redirection immédiate vers le guichet de règlement sécurisé
        window.location.href = pUrl;
      } else {
        throw new Error('Lien de paiement non reçu de la passerelle.');
      }
    } catch (err: any) {
      console.error('[Payment Gateway Error]:', err);
      setErrorMsg(err.message || 'Échec de connexion à la passerelle de paiement.');
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/60 backdrop-blur-xs">
          {/* Overlay click to close (toujours réactif) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 cursor-pointer"
            onClick={handleModalClose}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl border border-slate-200/90 shadow-2xl overflow-hidden flex flex-col z-10 max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header compact & soigné façon page AI Agent */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/60">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-500/20">
                  <Zap className="w-4 h-4 text-emerald-600 fill-emerald-500/30" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight">
                    Règlement sécurisé
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Mobile Money • Paiement sécurisé
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleModalClose}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-500 flex items-center justify-center transition-all cursor-pointer"
                title="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Corps du modal selon le statut */}
            {isCheckingStatus ? (
              /* Écran de vérification active */
              <div className="p-8 text-center space-y-4">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shadow-xs">
                  <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
                </div>
                <div className="space-y-1.5">
                  <h4 className="text-base font-bold text-slate-900">
                    Vérification du règlement...
                  </h4>
                  <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                    Interrogation de l'opérateur Mobile Money pour confirmer l'état de votre transaction.
                  </p>
                </div>
              </div>
            ) : verificationResult?.status === 'paid' ? (
              /* Écran de succès si payé */
              <div className="p-6 sm:p-7 text-center space-y-4 overflow-y-auto">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center border border-emerald-200/80 shadow-xs">
                  <CheckCircle2 className="w-9 h-9 text-emerald-600 stroke-[2.5]" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200/60 inline-block">
                    Confirmation réussie
                  </span>
                  <h4 className="text-lg font-black text-slate-950 tracking-tight pt-1">
                    Paiement validé avec succès !
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
                    Votre règlement de <span className="font-bold text-slate-900">{amountXof.toLocaleString('fr-FR')} FCFA</span> pour {title} a bien été validé.
                  </p>
                </div>

                <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-2xl p-3.5 text-xs text-emerald-900 flex items-center gap-3 text-left">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  <p className="leading-snug">
                    Votre forfait est immédiatement actif. Vos quotas de réponses et fonctionnalités sont débloqués.
                  </p>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleModalClose();
                      if (onNavigate) {
                        onNavigate('billing');
                      } else {
                        window.location.href = '/billing';
                      }
                    }}
                    className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.99] text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer transition-all shadow-md shadow-emerald-500/10"
                  >
                    <span>Accéder à ma facturation</span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                  </button>
                </div>
              </div>
            ) : verificationResult?.status === 'pending' ? (
              /* Écran si retour arrière alors que la transaction est encore en attente */
              <div className="p-6 sm:p-7 text-center space-y-4 overflow-y-auto">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200/80 shadow-xs">
                  <Clock className="w-7 h-7 text-amber-600 stroke-[2.2]" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200/60 inline-block">
                    En attente de confirmation
                  </span>
                  <h4 className="text-base sm:text-lg font-bold text-slate-950 tracking-tight pt-1">
                    Paiement en attente
                  </h4>
                  <p className="text-xs text-slate-600 max-w-sm mx-auto leading-relaxed">
                    Si vous avez validé votre code secret Mobile Money sur votre téléphone, le réseau peut mettre quelques instants à confirmer la réception.
                  </p>
                </div>

                {activeOrderRef && (
                  <div className="text-[11px] font-mono text-slate-500 bg-slate-50 py-1 px-3 rounded-lg border border-slate-200/60 inline-block">
                    Réf : {activeOrderRef}
                  </div>
                )}

                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    onClick={() => activeOrderRef && verifyPaymentStatus(activeOrderRef, true)}
                    disabled={isCheckingStatus}
                    className="w-full py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer transition-all shadow-md shadow-emerald-500/10"
                  >
                    <RefreshCw className={`w-4 h-4 stroke-[2.5] ${isCheckingStatus ? 'animate-spin' : ''}`} />
                    <span>Vérifier si le paiement est validé</span>
                  </button>

                  {activePaymentUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = activePaymentUrl;
                      }}
                      className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 active:scale-[0.99] text-slate-800 font-semibold text-xs flex items-center justify-center gap-2 cursor-pointer transition-all"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                      <span>Retourner sur la page de paiement</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full text-xs text-slate-500 hover:text-slate-800 font-medium py-2 transition-colors cursor-pointer"
                  >
                    Recommencer ou choisir un autre forfait
                  </button>
                </div>
              </div>
            ) : verificationResult?.status === 'cancelled' || verificationResult?.status === 'failed' ? (
              /* Écran si annulé ou échoué */
              <div className="p-6 sm:p-7 text-center space-y-4 overflow-y-auto">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-200/80 shadow-xs">
                  <AlertCircle className="w-7 h-7 text-rose-600 stroke-[2.2]" />
                </div>
                <div className="space-y-1.5">
                  <h4 className="text-base sm:text-lg font-bold text-slate-950 tracking-tight">
                    {verificationResult.status === 'cancelled' ? 'Transaction annulée' : 'Paiement non finalisé'}
                  </h4>
                  <p className="text-xs text-slate-600 max-w-sm mx-auto leading-relaxed">
                    {verificationResult.message || "Aucun montant n'a été débité de votre compte Mobile Money."}
                  </p>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full py-3 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 active:scale-[0.99] text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer transition-all shadow-xs"
                  >
                    <RefreshCw className="w-4 h-4 stroke-[2.2]" />
                    <span>Réessayer le règlement</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs cursor-pointer transition-all"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              /* Formulaire initial de confirmation */
              <form onSubmit={handlePay} className="p-5 sm:p-6 space-y-4 overflow-y-auto">
                {/* Carte récapitulative élégante & épurée */}
                <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Offre sélectionnée
                      </span>
                      <h4 className="text-sm sm:text-base font-bold text-slate-900 leading-snug">
                        {title}
                      </h4>
                    </div>
                    {type === 'plan' ? (
                      <span className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-100/70 text-emerald-800 border border-emerald-200/50">
                        {billingCycle === 'annual' ? 'Annuel (-20%)' : 'Mensuel'}
                      </span>
                    ) : type === 'whatsapp_addon' ? (
                      <span className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200/50">
                        Numéros WhatsApp
                      </span>
                    ) : (
                      <span className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200/50">
                        Recharge Réponses
                      </span>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-200/70 flex items-baseline justify-between">
                    <span className="text-xs font-medium text-slate-600">Total à payer :</span>
                    <div className="text-right">
                      <span className="text-2xl sm:text-3xl font-black text-slate-950 font-mono tracking-tight">
                        {amountXof.toLocaleString('fr-FR')}
                      </span>
                      <span className="text-xs font-bold text-emerald-600 ml-1.5 font-mono">FCFA</span>
                    </div>
                  </div>
                </div>

                {/* Logos des opérateurs supportés */}
                <div className="space-y-2">
                  <span className="text-[11px] font-medium text-slate-400 block">
                    Moyens acceptés sur la page de paiement :
                  </span>
                  <div className="flex flex-wrap items-center gap-3">
                    {SUPPORTED_OPERATORS.map((op) => (
                      <img
                        key={op.name}
                        src={op.logo}
                        alt={op.name}
                        title={op.name}
                        className="h-7 w-auto object-contain opacity-80 rounded-lg"
                      />
                    ))}
                  </div>
                </div>

                {/* Message d'erreur éventuel */}
                {errorMsg && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200/80 text-red-700 text-xs flex items-start gap-2.5 animate-in fade-in">
                    <span className="font-bold">•</span>
                    <span>{errorMsg}</span>
                  </div>
                )}

                {/* Actions & Bouton principal */}
                <div className="space-y-2.5 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer transition-all shadow-md shadow-emerald-500/10"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-slate-950" />
                        <span>Connexion sécurisée à la passerelle...</span>
                      </>
                    ) : (
                      <>
                        <span>Valider & Payer ({amountXof.toLocaleString('fr-FR')} FCFA)</span>
                        <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Chiffrement SSL 256-bit • Transaction sécurisée</span>
                  </div>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Alias de rétrocompatibilité
export const FusionPayModal = PaymentModal;
